import React, { useEffect, useState } from 'react';
import { BellRing, BellOff, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const IS_IOS = /iPhone|iPad|iPod/i.test(ua);
const IN_APP = /WhatsApp|FBAN|FBAV|Instagram|Line\/|; wv\)/i.test(ua);
const STANDALONE = typeof window !== 'undefined' &&
  (window.navigator.standalone || window.matchMedia?.('(display-mode: standalone)').matches);

function Steps({ items, testId }) {
  return (
    <ol className="mt-2 space-y-1 text-[13px] leading-snug list-decimal pl-5" data-testid={testId}>
      {items.map((s) => <li key={s}>{s}</li>)}
    </ol>
  );
}

export default function NotifyBanner({ planId, serverState, onChange }) {
  const { isSupported, isSubscribed, permission, subscribe } = usePushNotifications();
  const [busy, setBusy] = useState(false);
  const on = permission === 'granted' && isSubscribed;

  useEffect(() => {
    const state = !isSupported ? (IN_APP ? 'inapp' : 'unsupported') : on ? 'granted' : permission;
    if (state !== serverState) {
      api.post(`/patient/recovery/${planId}/notif-state`, { state }).then(({ data }) => onChange?.(data.state)).catch(() => {});
    }
  }, [isSupported, on, permission, planId, serverState, onChange]);

  if (on) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-[13px] font-semibold" data-testid="recovery-reminders-on">
        <CheckCircle2 size={16} /> Notifications ON — keep this page open for medicine reminders.
      </div>
    );
  }

  const allow = async () => {
    setBusy(true);
    const ok = await subscribe();
    setBusy(false);
    if (ok) toast.success('Reminders switched on');
    else toast.error('Notifications were not allowed');
  };

  let body;
  if (IN_APP) {
    body = <Steps testId="recovery-notify-inapp-steps" items={IS_IOS
      ? ['Tap the ⋯ / Share button at the top or bottom', 'Choose “Open in Safari”', 'Then follow the steps shown there']
      : ['Tap ⋮ at the top-right corner', 'Choose “Open in Chrome”', 'Then tap ALLOW NOTIFICATIONS']} />;
  } else if (IS_IOS && !STANDALONE) {
    body = <Steps testId="recovery-notify-ios-steps" items={[
      'Tap the Share button (square with an arrow) in Safari',
      'Choose “Add to Home Screen”, then tap Add',
      'Open DocSite from the new icon and tap ALLOW NOTIFICATIONS']} />;
  } else if (permission === 'denied') {
    body = <Steps testId="recovery-notify-blocked-steps" items={IS_IOS
      ? ['Open iPhone Settings → Notifications', 'Find DocSite and switch “Allow Notifications” on', 'Come back and refresh this page']
      : ['Tap the lock icon next to the web address', 'Tap Permissions → Notifications → Allow', 'Refresh this page']} />;
  } else if (!isSupported) {
    body = <p className="mt-2 text-[13px]">This browser cannot show reminders. Open this link in Chrome (Android) or Safari (iPhone).</p>;
  } else {
    body = (
      <button type="button" onClick={allow} disabled={busy}
              className="mt-3 w-full py-3.5 rounded-xl bg-white text-red-700 font-extrabold text-[15px] tracking-wide flex items-center justify-center gap-2 active:scale-[.98] transition-transform"
              data-testid="recovery-allow-notifications">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <BellRing size={18} />} ALLOW NOTIFICATIONS
      </button>
    );
  }

  return (
    <div className="rounded-2xl p-4 text-white shadow-lg" style={{ background: 'linear-gradient(135deg,#b91c1c,#c2410c)' }} data-testid="recovery-notify-banner">
      <div className="flex items-start gap-2">
        <BellOff size={20} className="shrink-0 mt-0.5" />
        <div className="text-[14px] font-extrabold leading-snug">
          IMPORTANT: Allow notifications so your surgical team can reach you instantly, and keep this page open to get medicine reminders.
        </div>
      </div>
      {body}
    </div>
  );
}
